import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response, Signature } from '@ui/src';
import { Order, PaymentOrder } from '../../../cash-collection/types';
import { transactionApi } from '../../../transactions/store/services';

import ROUTES from '../../../../routes';

export interface ImportTransactionListState {
  importHistory?: PaymentOrder[];
  pagination?: Pagination;
  importItem?: PaymentOrder;
  orders?: Order[];
  signatures?: Signature[];
  takeCount?: string;
  errorsList?: string;
  ordersErrorsList?: string;
}

const initialState: ImportTransactionListState = {};

export const importTransactionListSlice = createSlice({
  name: 'importTransactionList',
  initialState,
  reducers: {
    setStoreTakeCount: (state, action: PayloadAction<string>) => {
      state.takeCount = action.payload;
    },
    clearImportTransactionListState: (state) => {
      state.importHistory = undefined;
      state.pagination = undefined;
      state.takeCount = undefined;
      state.errorsList = undefined;
    },
    clearImportTransactionSidepanelState: (state) => {
      state.importItem = undefined;
      state.orders = undefined;
      state.signatures = undefined;
      state.ordersErrorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportHistory.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportHistory.matchRejected,
      (state, action: any) => {
        state.importHistory = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportHistory.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentOrder>>) => {
        state.importHistory = action.payload.data.edges?.map((item) => ({
          ...item.node,
          acceptedTransactions: item.node.beneficiariesCount.accepted,
          allTransactions: item.node.beneficiariesCount.all,
          procTransactions: item.node.beneficiariesCount.proc,
          rejectedTransactions: item.node.beneficiariesCount.rejected,
          url: generatePath(ROUTES.IMPORT_ITEM, {
            transactionId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.rejectImportItem.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.importHistory = state.importHistory?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.status = action.payload.data.status;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getImportItemSignatures.matchFulfilled,
      (state, action: PayloadAction<Response<Signature>>) => {
        state.signatures = action.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportItem.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.importItem = action.payload.data;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportItemOrders.matchPending,
      (state) => {
        state.ordersErrorsList = undefined;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportItemOrders.matchRejected,
      (state, action: any) => {
        state.orders = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.ordersErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.ordersErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportItemOrders.matchFulfilled,
      (state, action: PayloadAction<{ data: { beneficiaries: Order[] } }>) => {
        state.orders = action.payload.data.beneficiaries?.map((item) => ({
          ...item,
          originatorsIntermediaryBankName:
            item?.originatorsIntermediaryBankName || '',
        }));
      },
    );
  },
});

export const {
  clearImportTransactionListState,
  clearImportTransactionSidepanelState,
  setStoreTakeCount,
} = importTransactionListSlice.actions;

export default importTransactionListSlice.reducer;
